import type { ImageProps } from 'next/image';
import { getImageProps } from 'next/image';
import type { CSSProperties, Ref } from 'react';
import type { ImageFileType, ImageType } from '../../../interfaces/components';

/**
 * CmsImageItem Helper Functions
 *
 * This module provides utilities for optimized image rendering with responsive sizing.
 *
 * SIZING APPROACH:
 * Components accept sizing information from parent containers to avoid hardcoded assumptions.
 * Parent containers (groups, layouts) should provide appropriate sizing based on their context.
 *
 * USAGE EXAMPLES:
 *
 * // Default fallback (viewport width)
 * <OptimizedImages {...params} />
 *
 * // Parent provides custom sizes
 * <OptimizedImages {...params} sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw" />
 *
 * // Custom viewport-based sizing, from grid context
 * <OptimizedImages {...params} sizes="(min-width: 768px) 33vw, 100vw" />
 */

// ==================== DATA EXTRACTION ====================

/**
 * Extracts image objects from CMS data.
 * Prioritizes file array, falls back to image_default/image_mobile props.
 */
export const extractImageObjects = (
  file?: ImageFileType[],
  image_default?: ImageType,
  image_mobile?: ImageType,
) => {
  const imageObjDefault =
    file?.find(
      (f) => f.field_name === 'field_image_default' || f.field_name === 'field_svg_file',
    ) ??
    image_default ??
    null;

  const imageObjMobile =
    file?.find((f) => f.field_name === 'field_image_mobile') ?? image_mobile ?? null;

  return { imageObjDefault, imageObjMobile };
};

/**
 * Extracts width and height from image data object.
 * Returns null for both if data is invalid or missing.
 */
export const extractDimensions = (imageObj: ImageType | ImageFileType | null) => {
  if (!imageObj || Array.isArray(imageObj.data)) {
    return { width: null, height: null };
  }
  return {
    width: imageObj.data?.width ? String(imageObj.data.width) : null,
    height: imageObj.data?.height ? String(imageObj.data.height) : null,
  };
};

/**
 * Builds the image source URL, applying optimization transforms if enabled.
 */
export const buildImageSrc = (
  imageObj: ImageType | ImageFileType | null,
  imageOptimizationEnabled: boolean,
  uriTransformSource: RegExp,
  uriTransformEndpoint: string,
) => {
  if (!imageObj?.url) return '';
  if (imageOptimizationEnabled && typeof imageObj?.uri === 'string') {
    return imageObj.uri.replace(uriTransformSource, uriTransformEndpoint);
  }
  return imageObj.url;
};

// ==================== SIZING UTILITIES ====================

/**
 * Returns the sizes from the parent's column context, or the viewport width as the fallback.
 */
export const generateImageSizes = ({ sizes }: { sizes?: string }): string => {
  if (sizes) {
    return sizes;
  }

  // The content width is themed per tenant, so only the viewport bounds the slot. A smaller guess gets the image
  // stretched in the browser; a larger one costs at most the upload, because Next never enlarges an image.
  return '100vw';
};

// ==================== RENDERING ====================

/**
 * Renders a simple fallback image without optimization.
 * Used for SVGs or images without dimensions.
 */
export const FallbackImage = ({
  ref,
  defaultSrc,
  altText,
  lazyLoading,
  fieldDisplayVariant,
  borderRadius,
}: {
  ref: Ref<HTMLImageElement>;
  defaultSrc: string;
  altText: string;
  lazyLoading: boolean;
  fieldDisplayVariant?: string;
  borderRadius?: string;
}) => (
  <img
    ref={ref}
    src={defaultSrc}
    alt={altText}
    loading={lazyLoading ? 'lazy' : 'eager'}
    // INSPIRE-4974: same overflow fix as in CmsImageItem — see comment there for details.
    style={{
      borderRadius: fieldDisplayVariant === 'circle' ? '50%' : borderRadius,
      maxWidth: '100%',
    }}
  />
);

/**
 * Renders optimized images with responsive sources.
 * Creates a <picture> element if mobile variant exists, otherwise a single <img>.
 */
export const OptimizedImages = ({
  defaultSrc,
  defaultWidth,
  defaultHeight,
  mobileSrc,
  mobileWidth,
  mobileHeight,
  canOptimizeMobile,
  commonImageProps,
  ref,
  altText,
  className,
  style,
  sizes: explicitSizes,
}: {
  defaultSrc: string;
  defaultWidth: string;
  defaultHeight: string;
  mobileSrc: string | null;
  mobileWidth: string | null;
  mobileHeight: string | null;
  canOptimizeMobile: boolean;
  commonImageProps: Omit<ImageProps, 'src' | 'width' | 'height' | 'sizes'>;
  ref: Ref<HTMLImageElement>;
  altText: string;
  className?: string;
  style?: CSSProperties;
  sizes?: string;
}) => {
  const sizes = generateImageSizes({ sizes: explicitSizes });

  // Generate desktop image props
  const {
    props: { srcSet: desktopSrcSet, ...desktopProps },
  } = getImageProps({
    ...commonImageProps,
    src: defaultSrc,
    width: Number.parseInt(defaultWidth, 10),
    height: Number.parseInt(defaultHeight, 10),
    sizes,
  });

  // Generate mobile image props if available
  const mobileImageProps =
    canOptimizeMobile && mobileSrc && mobileWidth && mobileHeight
      ? getImageProps({
          ...commonImageProps,
          src: mobileSrc,
          width: Number.parseInt(mobileWidth, 10),
          height: Number.parseInt(mobileHeight, 10),
          sizes,
        })
      : null;

  // Render with or without mobile variant
  if (mobileImageProps) {
    return (
      <picture>
        <source
          media="(max-width: 600px)"
          srcSet={mobileImageProps.props.srcSet}
          sizes={mobileImageProps.props.sizes}
          width={mobileWidth ?? undefined}
          height={mobileHeight ?? undefined}
        />
        <img
          {...desktopProps}
          ref={ref}
          srcSet={desktopSrcSet}
          className={className}
          style={style}
          alt={altText}
        />
      </picture>
    );
  }

  return (
    <img
      {...desktopProps}
      ref={ref}
      srcSet={desktopSrcSet}
      className={className}
      style={style}
      alt={altText}
    />
  );
};
